// import { axios } from 'axios';
import { createSlice } from '@reduxjs/toolkit';
// utils
import customAxios from '../../utils/axios';
import { dispatch } from '../store';

// ----------------------------------------------------------------------

const initialState = {
  isLoading: false,
  error: null,
  rolesList: [],
  operatorData: [],
  projectData: [],
  clientsList: [],
  currentPage: 1,
  totalPages: 1,
  totalResults: 10,
  pageSize: 10,
  projectCurrentPage: null,
  projectTotalPages: null,
  projectTotalResults: null,
  clientTrades: {},
  scriptwiseTrades: {},
  activityLogs: {},
  currentScriptClientsTrade: {},
  operatorProfile: {},
  adminProfile: {},
  brokerProfile: {},
  clientProfile: {},
  projectProfile: {},
  userbalanceSummary: {},
  userMoneyDetails: {},
  userFileDetails: {},
  userFundHistory: [],
  riskyClients: {},
  weeklyPLofUsers: [],
  banScripts: [],
  superAdminBanScripts: [],
  broadCastMessage: {},
  flashMessages: {},
  userStatistics: {},
  // Custom Filters
  customFilters: [],
  appliedFilters: [],
  isFilterLoading: false,
  filterError: null,
  // Suspicious Ticks
  suspiciousTicks: {
    data: [],
    count: 0,
    filters: {},
    metadata: {},
    timestamp: null,
    timezone: {},
  },
  // Profit Summary
  profitSummary: {
    data: null,
    isLoading: false,
    error: null,
  },
  // Daily PL Summary
  dailyPLSummary: {
    data: [],
    isLoading: false,
    error: null,
    page: 1,
    limit: 20,
    totalPages: 1,
    totalItems: 0,
    sortKey: 'totalDailyPL',
    sortOrder: 'desc',
  },
  // Daily PL User Data
  dailyPLUserData: {
    data: [],
    scriptName: null,
    isLoading: false,
    error: null,
  },
};

const slice = createSlice({
  name: 'operator',
  initialState,
  reducers: {
    // START LOADING
    startLoading(state) {
      state.isLoading = true;
    },

    // STOP LOADING
    stopLoading(state) {
      state.isLoading = false;
    },

    // HAS ERROR
    hasError(state, action) {
      state.isLoading = false;
      state.error = action.payload;
    },

    // GET CLIENT TRADES
    getClientTradesSuccess(state, action) {
      state.isLoading = false;
      state.clientTrades = action.payload;
    },
    getScriptWiseTradeSuccess(state, action) {
      state.isLoading = false;
      state.scriptwiseTrades = action.payload;
    },
    getcurrentScriptClientTradeSuccess(state, action) {
      state.isLoading = false;
      state.currentScriptClientsTrade = action.payload;
    },
    // GET ROLESLIST
    getRoleListSuccess(state, action) {
      state.isLoading = false;
      state.rolesList = action?.payload;
    },

    getRiskyClientsListSuccess(state, action) {
      state.isLoading = false;
      state.riskyClients = action?.payload;
    },

    getWeeklyPLofUsers(state, action) {
      state.isLoading = false;
      state.weeklyPLofUsers = action?.payload;
    },

    broadCastMessageOperations(state, action) {
      state.isLoading = false;
      state.broadCastMessage = action?.payload;
    },

    getFlashMessages(state, action) {
      state.isLoading = false;
      state.flashMessages = action?.payload;
    },

    getBanScripts(state, action) {
      state.isLoading = false;
      state.banScripts = action?.payload;
    },

    getSuperAdminBanScripts(state, action) {
      state.isLoading = false;
      state.superAdminBanScripts = action?.payload;
    },

    // GET ADMIN
    getAdminProfileSuccess(state, action) {
      state.isLoading = false;
      state.adminProfile = action?.payload?.user;
    },

    // GET BROKER
    getBrokerProfileSuccess(state, action) {
      state.isLoading = false;
      state.brokerProfile = action?.payload?.user;
    },
    // GET CLIENT
    getClientProfileSuccess(state, action) {
      state.isLoading = false;
      state.clientProfile = action?.payload?.user;
    },
    // GET USERS
    getUsersSuccess(state, action) {
      state.isLoading = false;
      state.users = action?.payload?.docs;
      state.totalPages = action?.payload?.totalPages;
      state.currentPage = action?.payload?.page;
      state.totalResults = action?.payload?.totalResults;
    },

    // GET OPERATORS
    getOperatorListSuccess(state, action) {
      state.isLoading = false;
      state.operatorData = action?.payload?.docs;
      state.totalPages = action?.payload?.totalPages;
      state.currentPage = action?.payload?.page;
      state.totalResults = action?.payload?.totalDocs;
      state.pageSize = action?.payload?.limit;
    },
    getClientsListSuccess(state, action) {
      state.isLoading = false;
      state.clientsList = action?.payload?.docs;
      state.totalPages = action?.payload?.totalPages;
      state.currentPage = action?.payload?.page;
      state.totalResults = action?.payload?.totalDocs;
      state.pageSize = action?.payload?.limit;
    },

    getUserBalanceSummarySuccess(state, action) {
      state.isLoading = false;
      state.userbalanceSummary = action?.payload;
    },
    getUserMoneyDetailSuccess(state, action) {
      state.isLoading = false;
      state.userMoneyDetails = action?.payload;
    },

    getUserFilesSuccess(state, action) {
      state.isLoading = false;
      state.userFileDetails = action?.payload;
    },

    getUserFundHistorySuccess(state, action) {
      state.isLoading = false;
      state.userFundHistory = action?.payload;
    },

    getUserStatisticsSuccess(state, action) {
      state.isLoading = false;
      state.userStatistics = action?.payload;
    },

    // POST OPERATOR
    postOperatorSuccess(state) {
      state.isLoading = false;
    },

    // PUT OPERATOR
    updateOperatorListSuccess(state, action) {
      state.isLoading = false;
      const { operatorID, filterStatus, newStatus } = action.payload;
      if (filterStatus === 'all') {
        const findIndex = state.operatorData.findIndex((data) => data._id === operatorID);
        state.operatorData[findIndex].status = newStatus;
      } else {
        state.operatorData = state.operatorData.filter((data) => data._id !== operatorID);
      }
    },

    // GET OPERATOR PROFILE
    getOperatorProfileSuccess(state, action) {
      if (action?.payload?.loading) {
        state.isLoading = false;
      }
      state.operatorProfile = action?.payload?.user;
    },

    getActivityLogsSuccess(state, action) {
      state.isLoading = false;
      state.activityLogs = action.payload;
    },

    // GET OPERATOR PROJECT LIST

    getOperatorProjectListSuccess(state, action) {
      state.isLoading = false;
      state.projectData = action?.payload?.projects;
      state.projectProfile = {};
      state.totalPages = action?.payload?.totalPages;
      state.currentPage = action?.payload?.page;
      state.totalResults = action?.payload?.totalResults;
    },

    // PUT OPERATOR PROJECT
    updateOperatorProjectListSuccess(state, action) {
      state.isLoading = false;
      const { projectID, filterStatus, newStatus } = action.payload;
      if (filterStatus === 'all') {
        const findIndex = state.projectData.findIndex((data) => data._id === projectID);
        state.projectData[findIndex].status = newStatus;
      } else {
        state.projectData = state.projectData.filter((data) => data._id !== projectID);
      }
    },

    // GET OPERATOR PROJECT PROFILE
    getOperatorProjectProfileSuccess(state, action) {
      state.isLoading = false;
      state.projectProfile = action?.payload?.project;
    },

    // GET PROJECT DETAILS BY ID
    getProjectByIDSuccess(state, action) {
      state.isLoading = false;
      state.projectData = action?.payload?.projects;
      state.projectTotalPages = action?.payload?.totalPages;
      state.projectCurrentPage = action?.payload?.page;
      state.projectTotalResults = action?.payload?.totalResults;
    },

    // Custom Filters
    setCustomFilters(state, action) {
      state.customFilters = action.payload;
      state.isFilterLoading = false;
    },

    setAppliedFilters(state, action) {
      state.appliedFilters = action.payload;
    },

    startFilterLoading(state) {
      state.isFilterLoading = true;
    },

    stopFilterLoading(state) {
      state.isFilterLoading = false;
    },

    setFilterError(state, action) {
      state.filterError = action.payload;
      state.isFilterLoading = false;
    },

    addCustomFilter(state, action) {
      state.customFilters.push(action.payload);
      state.isFilterLoading = false;
    },

    updateCustomFilter(state, action) {
      const { id, ...updatedFilter } = action.payload;
      const index = state.customFilters.findIndex((filter) => filter._id === id);
      if (index !== -1) {
        state.customFilters[index] = { ...state.customFilters[index], ...updatedFilter };
      }
      state.isFilterLoading = false;
    },

    removeCustomFilter(state, action) {
      state.customFilters = state.customFilters.filter((filter) => filter._id !== action.payload);
      state.isFilterLoading = false;
    },

    // SUSPICIOUS TICKS
    getSuspiciousTicksSuccess(state, action) {
      state.isLoading = false;
      state.suspiciousTicks = action.payload;
    },

    // PROFIT SUMMARY
    startProfitSummaryLoading(state) {
      state.profitSummary.isLoading = true;
      state.profitSummary.error = null;
    },

    getProfitSummarySuccess(state, action) {
      state.profitSummary.isLoading = false;
      state.profitSummary.data = action.payload;
      state.profitSummary.error = null;
    },

    getProfitSummaryError(state, action) {
      state.profitSummary.isLoading = false;
      state.profitSummary.error = action.payload;
    },

    clearProfitSummary(state) {
      state.profitSummary.data = null;
      state.profitSummary.error = null;
    },

    // DAILY PL SUMMARY
    startDailyPLSummaryLoading(state) {
      state.dailyPLSummary.isLoading = true;
      state.dailyPLSummary.error = null;
    },

    getDailyPLSummarySuccess(state, action) {
      state.dailyPLSummary.isLoading = false;
      state.dailyPLSummary.data = action.payload.data;
      state.dailyPLSummary.page = action.payload.page || action.payload.currentPage || 1;
      state.dailyPLSummary.limit = action.payload.limit || 20;
      state.dailyPLSummary.totalPages = action.payload.totalPages || 1;
      state.dailyPLSummary.totalItems = action.payload.totalItems || action.payload.totalResults || 0;
      state.dailyPLSummary.sortKey = action.payload.sortKey || 'totalDailyPL';
      state.dailyPLSummary.sortOrder = action.payload.sortOrder || 'desc';
      state.dailyPLSummary.error = null;
    },

    getDailyPLSummaryError(state, action) {
      state.dailyPLSummary.isLoading = false;
      state.dailyPLSummary.error = action.payload;
    },

    clearDailyPLSummary(state) {
      state.dailyPLSummary.data = [];
      state.dailyPLSummary.error = null;
      state.dailyPLSummary.page = 1;
      state.dailyPLSummary.limit = 20;
      state.dailyPLSummary.totalPages = 1;
      state.dailyPLSummary.totalItems = 0;
      state.dailyPLSummary.sortKey = 'totalDailyPL';
      state.dailyPLSummary.sortOrder = 'desc';
    },

    // DAILY PL USER DATA
    startDailyPLUserDataLoading(state) {
      state.dailyPLUserData.isLoading = true;
      state.dailyPLUserData.error = null;
    },

    getDailyPLUserDataSuccess(state, action) {
      state.dailyPLUserData.data = action.payload.data;
      state.dailyPLUserData.scriptName = action.payload.scriptName;
      state.dailyPLUserData.isLoading = false;
      state.dailyPLUserData.error = null;
    },

    getDailyPLUserDataError(state, action) {
      state.dailyPLUserData.error = action.payload;
      state.dailyPLUserData.isLoading = false;
    },

    clearDailyPLUserData(state) {
      state.dailyPLUserData.data = [];
      state.dailyPLUserData.scriptName = null;
      state.dailyPLUserData.error = null;
    },
  },
});

// Reducer
export default slice.reducer;

// Actions
export const operatorActions = slice.actions;

// ----------------------------------------------------------------------

// GET CLIENTS TRADER
// export function getClientsTrade(pageSize, pageNumber, sortBy, sortOrder, searchWord, type) {
//   return async () => {
//     let response;
//     try {
//       dispatch(slice.actions.startLoading());

//       const queryParams = new URLSearchParams({
//         pageSize,
//         page: pageNumber,
//         sortOrder,
//         sortBy,
//       });

//       if (searchWord?.length > 0) {
//         queryParams.append('search', searchWord);
//       }
//       if (type.length > 0) {
//         queryParams.append('type', type);
//       }

//       const url = `/user/clients/trades?${queryParams.toString()}`;

//       response = await customAxios.get(url);

//       dispatch(slice.actions.getClientTradesSuccess(response?.data?.data));
//     } catch (error) {
//       dispatch(slice.actions.hasError(error?.response?.data));
//       // if (customAxios.isCancel(error)) {
//       //   throw error?.response?.data;
//       // }
//     }
//   };
// }

export function getClientsTrade(pageSize, pageNumber, sortBy, sortOrder, filterName, type) {
  return async (dispatch) => {
    let response;
    try {
      dispatch(slice.actions.startLoading());

      // Initialize query parameters
      const queryParams = new URLSearchParams({
        pageSize,
        page: pageNumber,
        sortOrder,
        sortBy,
      });

      // Append searchWord if it exists
      if (filterName?.tradingSymbol) {
        queryParams.append('tradingSymbol', filterName.tradingSymbol);
      }

      if (filterName?.name) {
        queryParams.append('name', filterName.name);
      }

      // Append segments individually
      if (filterName?.segments?.length > 0) {
        filterName.segments.forEach((segment) => {
          queryParams.append('segements', segment);
        });
      }

      // Append statuses individually
      if (filterName?.statuses?.length > 0) {
        filterName.statuses.forEach((status) => {
          queryParams.append('statuses', status);
        });
      }

      // Append boughtAt and soldAt if they exist
      if (filterName?.boughtAt) {
        queryParams.append('boughtAt', filterName.boughtAt);
      }

      if (filterName?.soldAt) {
        queryParams.append('soldAt', filterName.soldAt);
      }

      // Append type if it exists
      if (type?.length > 0) {
        queryParams.append('type', type);
      }

      // Generate the final URL with query params
      const url = `/user/clients/trades?${queryParams.toString()}`;

      // Perform the request
      response = await customAxios.get(url);

      // Dispatch the success action with the response data
      dispatch(slice.actions.getClientTradesSuccess(response?.data?.data));
    } catch (error) {
      // Handle errors
      dispatch(slice.actions.hasError(error?.response?.data));
    }
  };
}

export function getWeeklyPLofUsers({ selectedRole, isoWeekCode, year }) {
  return async () => {
    let response;
    try {
      const queryParams = new URLSearchParams({
        role: selectedRole,
        week: isoWeekCode,
        year,
      });
      await dispatch(slice.actions.startLoading());
      const url = `/weekly-report/profits?${queryParams}`;
      response = await customAxios.get(url);
      await dispatch(slice.actions.getWeeklyPLofUsers(response?.data));
    } catch (error) {
      await dispatch(slice.actions.hasError(error?.response?.data));
    }
  };
}

// export function getClientLineTrades(pageSize, pageNumber, sortBy, sortOrder, searchWord, timeDifference = 5) {
//   return async () => {
//     let response;
//     try {
//       dispatch(slice.actions.startLoading());
//       const queryParams = new URLSearchParams({
//         pageSize,
//         page: pageNumber,
//         sortOrder,
//         sortBy,
//         timeDifference,
//       });

//       if (searchWord?.length > 0) {
//         queryParams.append('search', searchWord);
//       }

//       const url = `/user/clients/line-trades?${queryParams.toString()}`;

//       response = await customAxios.get(url);

//       dispatch(slice.actions.getClientTradesSuccess(response?.data?.data));
//     } catch (error) {
//       dispatch(slice.actions.hasError(error?.response?.data));
//       // if (customAxios.isCancel(error)) {
//       //   throw error?.response?.data;
//       // }
//     }
//   };
// }
export function getClientLineTrades(pageSize, pageNumber, sortBy, sortOrder, customFilters = {}) {
  return async (dispatch) => {
    let response;
    try {
      dispatch(slice.actions.startLoading());

      // Build URLSearchParams with only pagination and sorting
      const queryParams = new URLSearchParams({
        pageSize,
        page: pageNumber,
        sortOrder,
        sortBy,
      });

      // Handle custom MongoDB filters only
      if (customFilters && Object.keys(customFilters).length > 0) {
        queryParams.append('customFilters', JSON.stringify(customFilters));
      }

      const url = `/user/clients/line-trades?${queryParams.toString()}`;

      response = await customAxios.get(url);

      dispatch(slice.actions.getClientTradesSuccess(response?.data?.data));
    } catch (error) {
      dispatch(slice.actions.hasError(error?.response?.data));
    }
  };
}

// UPDATE USER BALANCE
export function updateUserBalance(userId, amount, operation) {
  return async () => {
    await dispatch(slice.actions.startLoading());
    try {
      const response = await customAxios.put(`/user/balance/update`, { userId, amount, operation });
      await dispatch(slice.actions.stopLoading());
      return response.data;
    } catch (err) {
      dispatch(slice.actions.hasError(err));
      console.log('ERRROR', err);
      throw err;
    }
  };
}

// Thunk to get all ban scripts by super admin
export function getAllBanScripts() {
  return async (dispatch) => {
    try {
      dispatch(slice.actions.startLoading());
      const response = await customAxios.get('/script/all-ban-list');
      dispatch(slice.actions.getSuperAdminBanScripts(response.data.banScripts));
    } catch (error) {
      dispatch(slice.actions.hasError(error.response.data));
    }
  };
}

// Thunk to set a ban script
export function setBanScript(banScripts) {
  return async (dispatch) => {
    try {
      dispatch(slice.actions.startLoading());
      const response = await customAxios.post('/script/set-ban', { banScripts });
      dispatch(slice.actions.stopLoading());
      return response.data;
    } catch (error) {
      dispatch(slice.actions.hasError(error.response.data));
      throw error;
    }
  };
}

export function broadCastMessageOperations(payload, isEdit, messageId, flash) {
  return async () => {
    let response;
    try {
      await dispatch(slice.actions.startLoading());

      if (isEdit && messageId) {
        await customAxios.put(`/message/${messageId}`, payload);
        response = await customAxios.get(`/message`);
      } else if (payload) {
        await customAxios.post(`/message`, payload);
        response = await customAxios.get(`/message`);
      } else if (messageId) {
        await customAxios.delete(`/message/${messageId}`);
        response = await customAxios.get(`/message`);
      } else if (flash) {
        response = await customAxios.get(`/message?flash=true`);
      } else {
        response = await customAxios.get(`/message`);
      }

      await dispatch(slice.actions.broadCastMessageOperations(response?.data));
    } catch (error) {
      await dispatch(slice.actions.hasError(error?.response?.data));
    }
  };
}

export function getFlashMessages() {
  return async () => {
    let response;
    try {
      await dispatch(slice.actions.startLoading());

      response = await customAxios.get(`/message?flash=true`);

      await dispatch(slice.actions.getFlashMessages(response?.data));
    } catch (error) {
      await dispatch(slice.actions.hasError(error?.response?.data));
    }
  };
}

export function getBanScripts() {
  return async (dispatch) => {
    try {
      // Start the loading state
      dispatch(slice.actions.startLoading());

      // // Axios instance with custom headers
      // const apiClient = axios.create({
      //   baseURL: 'https://webapi.niftytrader.in/webapi',
      //   timeout: 10000,
      //   headers: {
      //     'Content-Type': 'application/json',
      //     Accept: 'application/json, text/plain, */*',
      //     'Accept-Encoding': 'gzip, deflate, br, zstd',
      //     'User-Agent':
      //       'Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/131.0.0.0 Safari/537.36',
      //     platform_type: 1,
      //     Origin: 'https://www.niftytrader.in',
      //     Referer: 'https://www.niftytrader.in/',
      //     Connection: 'keep-alive',
      //     'sec-ch-ua': '"Google Chrome";v="131", "Chromium";v="131", "Not_A Brand";v="24"',
      //     'sec-ch-ua-mobile': '?0',
      //     'sec-ch-ua-platform': '"Windows"',
      //     'Sec-Fetch-Site': 'same-site',
      //     'Sec-Fetch-Mode': 'cors',
      //     'Sec-Fetch-Dest': 'empty',
      //   },
      // });

      // API call
      const response = await customAxios.get('/script/ban-list');

      // Dispatch success action with response data
      dispatch(slice.actions.getBanScripts(response?.data));
    } catch (error) {
      // Dispatch error action
      dispatch(slice.actions.hasError(error?.response?.data));
    }
  };
}

// GET OPERATOR DETAILS
export function getOperator(managerId, pageSize, pageNumber, statusData, order, searchWord, cancelToken) {
  return async () => {
    let response;
    try {
      await dispatch(slice.actions.startLoading());
      if (searchWord?.length > 0) {
        response = await customAxios.get(
          `/user?managerId=${managerId}&status=${statusData}&order=${order}&pageSize=${pageSize}&page=${pageNumber}&search=${searchWord}`,
          { cancelToken: cancelToken.token }
        );
      } else {
        // response = await customAxios.get(
        //   `/user?managerId=${managerId}&status=${statusData}&order=${order}&pageSize=${pageSize}&page=${pageNumber}`
        // );
        response = await customAxios.get(`/user?managerId=${managerId}`);
      }
      // const { message } = response?.data;
      await dispatch(slice.actions.getOperatorListSuccess(response?.data));
    } catch (error) {
      // await dispatch(slice.actions.hasError(error?.response?.data));
    }
  };
}
export function getClients({ userId, pageSize = 10, pageNumber = 1, sortBy, sortOrder, searchWord, isActive }) {
  return async () => {
    let response;
    try {
      await dispatch(slice.actions.startLoading());
      const queryParams = new URLSearchParams({
        pageSize,
        page: pageNumber,
        sortOrder,
        sortBy,
      });
      if (searchWord?.length > 0) {
        queryParams.append('search', searchWord);
      }
      if (userId) {
        queryParams.append('userId', userId);
      }
      if (isActive !== undefined && isActive !== null) {
        queryParams.append('isActive', isActive);
      }
      const url = `/user/clients/all?${queryParams.toString()}`;
      response = await customAxios.get(url);
      // if (searchWord?.length > 0) {
      //   response = await customAxios.get(
      //     `/user/clients/all?status=${statusData}&order=${order}&pageSize=${pageSize}&page=${pageNumber}&searchTerm=${searchWord}`
      //   );
      // } else {
      //   // response = await customAxios.get(
      //   //   `/user?managerId=${managerId}&status=${statusData}&order=${order}&pageSize=${pageSize}&page=${pageNumber}`
      //   // );
      //   response = await customAxios.get(
      //     `/user/clients/all?status=${statusData}&order=${order}&pageSize=${pageSize}&page=${pageNumber}&searchTerm=${searchWord}`
      //   );
      // }
      // const { message } = response?.data;
      await dispatch(slice.actions.getClientsListSuccess(response?.data?.data));
    } catch (error) {
      // await dispatch(slice.actions.hasError(error?.response?.data));
    }
  };
}

// SEARCH CLIENTS FOR DROPDOWN (Lightweight API call)
export function searchClientsDropdown(searchTerm, pageSize = 10) {
  return async () => {
    try {
      if (!searchTerm || searchTerm.trim().length === 0) {
        return { success: true, data: [], pagination: {} };
      }

      const queryParams = new URLSearchParams({
        search: searchTerm,
        pageSize,
        page: 1,
      });

      const response = await customAxios.get(`/user/clients/all?${queryParams.toString()}`);

      if (response?.data?.data) {
        // Extract pagination info from response
        const paginationData = {
          currentPage: response?.data?.currentPage || 1,
          totalPages: response?.data?.totalPages || 1,
          totalResults: response?.data?.totalResults || response?.data?.data?.length,
          pageSize,
        };

        return {
          success: true,
          data: response?.data?.data,
          pagination: paginationData,
        };
      }

      return { success: true, data: [], pagination: {} };
    } catch (error) {
      console.error('Search clients error:', error);
      return {
        success: false,
        error: error?.response?.data?.message || 'Failed to search clients',
        data: [],
      };
    }
  };
}

// SEARCH OPERATORS FOR DROPDOWN (Lightweight API call)
export function searchOperatorsDropdown(searchTerm, pageSize = 10) {
  return async () => {
    try {
      if (!searchTerm || searchTerm.trim().length === 0) {
        return { success: true, data: [], pagination: {} };
      }

      const queryParams = new URLSearchParams({
        search: searchTerm,
        pageSize,
        page: 1,
      });

      const response = await customAxios.get(`/user?${queryParams.toString()}`);

      if (response?.data) {
        const paginationData = {
          currentPage: response?.data?.currentPage || 1,
          totalPages: response?.data?.totalPages || 1,
          totalResults: response?.data?.totalResults || response?.data?.length,
          pageSize,
        };

        return {
          success: true,
          data: response?.data?.data || response?.data,
          pagination: paginationData,
        };
      }

      return { success: true, data: [], pagination: {} };
    } catch (error) {
      console.error('Search operators error:', error);
      return {
        success: false,
        error: error?.response?.data?.message || 'Failed to search operators',
        data: [],
      };
    }
  };
}

export function getRiskyClients({
  pageSize = 10,
  pageNumber = 1,
  sortBy,
  sortOrder,
  searchWord,
  balancePercentage = 20,
}) {
  return async () => {
    let response;
    try {
      await dispatch(slice.actions.startLoading());
      const queryParams = new URLSearchParams({
        pageSize,
        page: pageNumber,
        sortOrder,
        sortBy,
        balancePercentage,
      });
      if (searchWord?.length > 0) {
        queryParams.append('search', searchWord);
      }
      const url = `/user/risky-clients/all?${queryParams.toString()}`;
      response = await customAxios.get(url);
      await dispatch(slice.actions.getRiskyClientsListSuccess(response?.data?.data));
    } catch (error) {
      // await dispatch(slice.actions.hasError(error?.response?.data));
    }
  };
}

// PUT OPERATORS
export function putOperator(operatorID, statusTab, notes, filterStatus) {
  return async () => {
    await dispatch(slice.actions.startLoading());
    try {
      const response = await customAxios.put(`/admin/user/${operatorID}?${statusTab}=true`);
      const { status, newStatus, message } = response?.data;
      if (status === 200) {
        await dispatch(slice.actions.updateOperatorListSuccess({ operatorID, statusTab, filterStatus, newStatus }));
      } else {
        await dispatch(slice.actions.hasError(message));
      }
    } catch (error) {
      await dispatch(slice.actions.hasError(error));
    }
  };
}

export function getActivityLogs({
  pageSize,
  page,
  type,
  sortOrder,
  sortBy,
  description,
  createdBy,
  forUser,
  startDate,
  endDate,
  userRoles,
  statuses,
}) {
  return async (dispatch) => {
    await dispatch(slice.actions.startLoading());
    try {
      const queryParamsObj = new URLSearchParams({
        pageSize,
        page,
        sortOrder,
        sortBy,
        type,
      });

      // Conditionally add optional fields if they are defined
      if (description !== undefined) queryParamsObj.set('description', description);
      if (createdBy !== undefined) queryParamsObj.set('createdBy', createdBy);
      if (forUser !== undefined) queryParamsObj.set('forUser', forUser);
      if (userRoles !== undefined) queryParamsObj.set('userRoles', userRoles);
      if (statuses !== undefined) queryParamsObj.set('statuses', statuses);

      // Handle startDate and endDate conditions
      if (
        startDate !== undefined &&
        endDate !== undefined &&
        !(startDate === '1970-01-01' && endDate === '1970-01-01')
      ) {
        queryParamsObj.set('startDate', startDate);
        queryParamsObj.set('endDate', endDate);
      }
      // Prepare query parameters

      const response = await customAxios.get(`/userlog/all?${queryParamsObj.toString()}`);
      dispatch(operatorActions.getActivityLogsSuccess(response.data.data));
    } catch (error) {
      await dispatch(slice.actions.hasError(error));
    }
  };
}

// DELETE ACTIVITY LOGS
export function deleteActivityLogs(ids) {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());
    try {
      const response = await customAxios.delete('/userlog/bulk-delete', {
        data: { ids },
      });
      if (response.status === 200) {
        return true;
      }
      dispatch(slice.actions.hasError(response.data.message));
      return false;
    } catch (error) {
      dispatch(slice.actions.hasError(error.message));
      return false;
    }
  };
}

export function getUserFiles({ pageSize, page, sortOrder, sortBy, search }) {
  return async () => {
    await dispatch(slice.actions.startLoading());
    try {
      const queryParams = new URLSearchParams({
        pageSize,
        page,
        sortOrder,
        sortBy,
      });

      if (search?.length > 0) {
        queryParams.append('search', search);
      }
      const response = await customAxios.get(`/userfile/all?${queryParams.toString()}`);
      dispatch(operatorActions.getUserFilesSuccess(response.data.data));
    } catch (error) {
      await dispatch(slice.actions.hasError(error));
    }
  };
}

// GET USER FUND HISTORY
export function getUserFundHistory({ userId, startDate, endDate, sortBy = 'createdAt', sortOrder = 'desc' }) {
  return async () => {
    await dispatch(slice.actions.startLoading());
    try {
      const params = {
        sortBy,
        sortOrder,
      };

      if (startDate) {
        params.startDate = startDate;
      }
      if (endDate) {
        params.endDate = endDate;
      }

      const response = await customAxios.get(`/fund-history/${userId}`, { params });

      if (response.data.success) {
        dispatch(slice.actions.getUserFundHistorySuccess(response.data.data));
      } else {
        dispatch(slice.actions.hasError(response.data.message || 'Failed to fetch fund history'));
      }
    } catch (error) {
      const errorMessage = error?.response?.data?.message || error?.message || 'Failed to fetch fund history';
      await dispatch(slice.actions.hasError(errorMessage));
    }
  };
}

// GET ROLES List
export function getRoles() {
  return async () => {
    dispatch(slice.actions.startLoading());
    try {
      const response = await customAxios.get('/roles');
      const { rolesList } = response?.data;
      dispatch(slice.actions.getRoleListSuccess(rolesList));
    } catch (error) {
      dispatch(slice.actions.hasError(error.message));
    }
  };
}

// GET USER
export function getUser(payload) {
  return async () => {
    dispatch(slice.actions.startLoading());
    try {
      await customAxios.get('/users', { ...payload });
    } catch (err) {
      dispatch(slice.actions.hasError(err));
    }
  };
}

// POST OPERATOR

export function postOperator(payload, isEdit, userId) {
  return async () => {
    dispatch(slice.actions.startLoading());
    try {
      let response;
      if (isEdit) {
        response = await customAxios.put(`user/edit/${userId}`, { ...payload });
      } else {
        response = await customAxios.post('/user/create', { ...payload });
      }
      const { status, message } = response?.data;
      if (status === 201) {
        dispatch(slice.actions.postOperatorSuccess());
      } else {
        dispatch(slice.actions.hasError(message));
      }
    } catch (error) {
      dispatch(slice.actions.hasError(error));
      throw error;
    }
  };
}

// GET OPERATOR PROFILE

export function getOperatorProfile(userId, loading = true) {
  return async () => {
    if (!loading) {
      await dispatch(slice.actions.startLoading());
    }
    try {
      const response = await customAxios.get(`user/profile/${userId}`);
      await dispatch(slice.actions.getOperatorProfileSuccess({ ...response.data, loading }));
    } catch (error) {
      await dispatch(slice.actions.hasError(error));
      throw error;
    }
  };
}

export function getAdminProfile(userId) {
  return async () => {
    await dispatch(slice.actions.startLoading());
    try {
      const response = await customAxios.get(`user/profile/${userId}`);
      await dispatch(slice.actions.getAdminProfileSuccess(response.data));
      return response.data;
    } catch (error) {
      await dispatch(slice.actions.hasError(error));
      throw error;
    }
  };
}
export function getBrokerProfile(userId) {
  return async () => {
    await dispatch(slice.actions.startLoading());
    try {
      const response = await customAxios.get(`user/profile/${userId}`);
      await dispatch(slice.actions.getBrokerProfileSuccess(response.data));
      return response.data;
    } catch (error) {
      await dispatch(slice.actions.hasError(error));
      throw error;
    }
  };
}
export function getClientProfile(userId) {
  return async () => {
    await dispatch(slice.actions.startLoading());
    try {
      const response = await customAxios.get(`user/profile/${userId}`);
      await dispatch(slice.actions.getClientProfileSuccess(response.data));
      return response.data;
    } catch (error) {
      await dispatch(slice.actions.hasError(error));
      throw error;
    }
  };
}

// GET OPERATOR PROJECT LIST
export function getProjectList(pageSize, pageNumber, statusData, order, searchWord, cancelToken) {
  return async () => {
    let response;
    try {
      if (searchWord?.length > 0) {
        response = await customAxios.get(
          `/admin/operator/project/?status=${statusData}&order=${order}&pageSize=${pageSize}&page=${pageNumber}&search=${searchWord}`,
          { cancelToken: cancelToken.token }
        );
      } else {
        await dispatch(slice.actions.startLoading());
        response = await customAxios.get(
          `/admin/operator/project/?status=${statusData}&order=${order}&pageSize=${pageSize}&page=${pageNumber}`
        );
      }
      const { data, status, message } = response?.data;
      if (status === 200) {
        await dispatch(slice.actions.getOperatorProjectListSuccess(data));
      } else {
        await dispatch(slice.actions.hasError(message));
      }
    } catch (error) {
      await dispatch(slice.actions.hasError(error));
      // if (axios.isCancel(error) || error) {
      //   // console.log(error);
      // }
    }
  };
}
// export function getScriptWiseTrade({
//   userId,
//   pageSize,
//   pageNumber,
//   statusData,
//   order,
//   sortBy,
//   searchWord,
//   type,
//   cancelToken,
//   segements=[],
// }) {
//   return async () => {
//     let response;
//     try {
//       await dispatch(slice.actions.startLoading());
//       if (searchWord?.length > 0) {
//         response = await customAxios.get(
//           `/trade/grouped-by-script/${userId}?status=${statusData}&sortOrder=${order}&sortBy=${sortBy}&pageSize=${pageSize}&page=${pageNumber}&search=${searchWord}&type=${type}`
//         );
//       } else {
//         // await dispatch(slice.actions.startLoading());
//         response = await customAxios.get(
//           `/trade/grouped-by-script/${userId}?status=${statusData}&sortOrder=${order}&sortBy=${sortBy}&pageSize=${pageSize}&page=${pageNumber}&type=${type}`
//         );
//       }
//       const { data, status, message } = response?.data;

//       if (status === 200) {
//         await dispatch(slice.actions.getScriptWiseTradeSuccess(data));
//       } else {
//         await dispatch(slice.actions.hasError(message));
//       }
//     } catch (error) {
//       if (cancelToken) {
//         cancelToken.cancel();
//       }
//     }
//   };
// }

export function getScriptWiseTrade({
  userId,
  pageSize,
  pageNumber,
  statusData,
  order,
  sortBy,
  searchWord,
  type,
  cancelToken,
  segements = [],
  applySharing = false,
}) {
  return async () => {
    let response;
    try {
      await dispatch(slice.actions.startLoading());

      // Create a new URLSearchParams object
      const queryParams = new URLSearchParams();

      // Append basic query parameters
      queryParams.append('status', statusData);
      queryParams.append('sortOrder', order);
      queryParams.append('sortBy', sortBy);
      queryParams.append('pageSize', pageSize);
      queryParams.append('page', pageNumber);
      queryParams.append('type', type);
      queryParams.append('applySharing', applySharing);

      // Append searchWord if available
      if (searchWord?.length > 0) {
        queryParams.append('search', searchWord);
      }

      if (segements.length > 0) {
        // Append segments as separate query parameters
        segements.forEach((segment) => {
          queryParams.append('segements', segment); // Use 'segments[]' to indicate an array
        });
      }
      // Construct the full URL
      const url = `/trade/grouped-by-script/${userId}?${queryParams.toString()}`;

      response = await customAxios.get(url);

      const { data, status, message } = response?.data;

      if (status === 200) {
        await dispatch(slice.actions.getScriptWiseTradeSuccess(data));
      } else {
        await dispatch(slice.actions.hasError(message));
      }
    } catch (error) {
      if (cancelToken) {
        cancelToken.cancel();
      }
      await dispatch(slice.actions.hasError(error.message));
    }
  };
}

export function getUserBalanceSummary({ userId, pageSize = 10, pageNumber = 1, sortBy, sortOrder, searchWord, applySharing = false, showDemo = false }) {
  return async () => {
    let response;
    try {
      dispatch(slice.actions.startLoading());
      const queryParams = new URLSearchParams({
        pageSize,
        page: pageNumber,
        sortOrder,
        sortBy,
        applySharing,
        showDemo,
      });

      if (searchWord?.length > 0) {
        queryParams.append('search', searchWord);
      }

      const url = `/user/clients/balance-summary${userId ? `/${userId}` : ''}?${queryParams.toString()}`;

      response = await customAxios.get(url);

      dispatch(slice.actions.getUserBalanceSummarySuccess(response?.data?.data));
      dispatch(slice.actions.getUserMoneyDetailSuccess(response?.data?.summary));
    } catch (error) {
      dispatch(slice.actions.hasError(error?.response?.data));
      // if (customAxios.isCancel(error)) {
      //   throw error?.response?.data;
      // }
    }
  };
}

export function getAllUsersTradesForCurrentScript({
  scriptId,
  pageSize,
  pageNumber,
  statusData,
  order,
  sortBy,
  searchWord,
  type,
  cancelToken,
}) {
  return async () => {
    let response;
    try {
      await dispatch(slice.actions.startLoading());
      if (searchWord?.length > 0) {
        response = await customAxios.get(
          `/trade/all/${scriptId}?status=${statusData}&sortOrder=${order}&sortBy=${sortBy}&pageSize=${pageSize}&page=${pageNumber}&search=${searchWord}&type=${type}`
        );
      } else {
        response = await customAxios.get(
          `/trade/all/${scriptId}?status=${statusData}&sortOrder=${order}&sortBy=${sortBy}&pageSize=${pageSize}&page=${pageNumber}&type=${type}`
        );
      }
      const { data, status, message } = response?.data;

      if (status === 200) {
        await dispatch(slice.actions.getcurrentScriptClientTradeSuccess(data));
      } else {
        await dispatch(slice.actions.hasError(message));
      }
    } catch (error) {
      await dispatch(slice.actions.hasError(error));
      if (cancelToken) {
        cancelToken.cancel();
      }
    }
  };
}

// PUT PROJECT

export function putProjectList(projectID, statusTab, notes, filterStatus) {
  return async () => {
    await dispatch(slice.actions.startLoading());
    try {
      const response = await customAxios.put(`/admin/operator/project/${projectID}?${statusTab}=true`);
      const { status, newStatus, message } = response?.data;
      if (status === 200) {
        await dispatch(
          slice.actions.updateOperatorProjectListSuccess({ projectID, statusTab, filterStatus, newStatus })
        );
      } else {
        await dispatch(slice.actions.hasError(message));
      }
    } catch (error) {
      await dispatch(slice.actions.hasError(error));
    }
  };
}

// GET OPERATOR PROJECT PROFILE

export function getOperatorProjectProfile(projectID) {
  return async () => {
    await dispatch(slice.actions.startLoading());
    try {
      const response = await customAxios.get(`admin/operator/project/${projectID}`);
      const { status, data, message } = response?.data;
      if (status === 200) {
        await dispatch(slice.actions.getOperatorProjectProfileSuccess(data));
      } else {
        await dispatch(slice.actions.hasError(message));
      }
    } catch (error) {
      await dispatch(slice.actions.hasError(error));
    }
  };
}

// GET PROJECT BY OPERATOR ID

export function getProjectByID(pageSize, pageNumber, statusData, order, id, searchWord, cancelToken) {
  return async () => {
    let response;
    try {
      if (searchWord?.length > 0) {
        response = await customAxios.get(
          `/admin/operator/project/list/${id}?status=${statusData}&order=${order}&pageSize=${pageSize}&page=${pageNumber}&search=${searchWord}`,
          { cancelToken: cancelToken.token }
        );
      } else {
        await dispatch(slice.actions.startLoading());
        response = await customAxios.get(
          `/admin/operator/project/list/${id}?status=${statusData}&order=${order}&pageSize=${pageSize}&page=${pageNumber}`
        );
      }
      const { data, status, message } = response?.data;
      if (status === 200) {
        await dispatch(slice.actions.getProjectByIDSuccess(data));
      } else {
        await dispatch(slice.actions.hasError(message));
      }
    } catch (error) {
      await dispatch(slice.actions.hasError(error));
      // if (axios.isCancel(error) || error) {
      //   // console.log(error);
      // }
    }
  };
}

// GET USER STATISTICS

export function getUserStatistics(userId) {
  return async () => {
    try {
      console.log('User Statistics Response:');
      await dispatch(slice.actions.startLoading());
      const response = await customAxios.get(`/user/statistics/${userId}`);
      const { status, data, message } = response?.data;
      if (status === 200) {
        await dispatch(slice.actions.getUserStatisticsSuccess(data));
      } else {
        await dispatch(slice.actions.hasError(message));
      }
    } catch (error) {
      await dispatch(slice.actions.hasError(error));
    }
  };
}

// GET SUSPICIOUS TICKS
export function getSuspiciousTicks({
  instrumentTokens = null,
  alertTypes = null,
  from = null,
  to = null,
  startPrice = null,
  endPrice = null,
  limit = null,
  page = null,
}) {
  return async () => {
    try {
      dispatch(slice.actions.startLoading());

      // Build query parameters
      const queryParams = new URLSearchParams();

      if (instrumentTokens && instrumentTokens.length > 0) {
        queryParams.append(
          'instrumentTokens',
          Array.isArray(instrumentTokens) ? instrumentTokens.join(',') : instrumentTokens
        );
      }

      if (alertTypes && alertTypes.length > 0) {
        queryParams.append('alertTypes', Array.isArray(alertTypes) ? alertTypes.join(',') : alertTypes);
      }

      if (from) {
        queryParams.append('from', from);
      }

      if (to) {
        queryParams.append('to', to);
      }

      if (startPrice !== null && startPrice !== undefined) {
        queryParams.append('startPrice', startPrice);
      }

      if (endPrice !== null && endPrice !== undefined) {
        queryParams.append('endPrice', endPrice);
      }

      if (limit !== null && limit !== undefined) {
        queryParams.append('limit', limit);
      }

      if (page !== null && page !== undefined) {
        queryParams.append('page', page);
      }

      // Direct fetch to external API
      const url = `https://live-fake-trade-detector-production.up.railway.app/api/TradeAlerts/suspicious?${queryParams.toString()}`;

      const fetchResponse = await fetch(url, {
        method: 'GET',
        headers: {
          Authorization: 'APIKEY mcE1cbX4JdRrYPEN7dVxg6dQNQv36Rl26ePujPMdr3caaET6exTBzA0bt07iK8Qs',
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
      });

      if (!fetchResponse.ok) {
        throw new Error(`HTTP error! status: ${fetchResponse.status}`);
      }

      const data = await fetchResponse.json();
      const response = { data };

      if (response.data) {
        dispatch(slice.actions.getSuspiciousTicksSuccess(response.data));
        return { success: true, data: response.data };
      }
      dispatch(slice.actions.hasError('No data received from suspicious ticks API'));
      return { success: false, error: 'No data received' };
    } catch (error) {
      const errorMessage = error?.response?.data?.message || error?.message || 'Failed to fetch suspicious ticks';
      dispatch(slice.actions.hasError(errorMessage));
      console.error('Suspicious Ticks API Error:', error);
      return { success: false, error: errorMessage };
    }
  };
}

// CUSTOM FILTERS THUNKS

// Get all custom filters for a specific type
export function getCustomFilters(filterType) {
  return async () => {
    try {
      dispatch(slice.actions.startFilterLoading());

      const queryParams = new URLSearchParams();
      if (filterType) {
        queryParams.append('type', filterType);
      }
      queryParams.append('includePublic', 'true');

      const response = await customAxios.get(`/custom-filters?${queryParams.toString()}`);
      const { status, data, message } = response?.data;

      if (status === 200) {
        dispatch(slice.actions.setCustomFilters(data?.docs || []));
      } else {
        dispatch(slice.actions.setFilterError(message));
      }
    } catch (error) {
      dispatch(slice.actions.setFilterError(error?.response?.data?.message || 'Failed to fetch filters'));
    }
  };
}

// Create a new custom filter
export function createCustomFilter(filterData) {
  return async () => {
    try {
      dispatch(slice.actions.startFilterLoading());

      const response = await customAxios.post('/custom-filters', filterData);
      const { status, data, message } = response?.data;
      if (status === 201) {
        dispatch(slice.actions.addCustomFilter(data));
        return { success: true, data };
      }
      dispatch(slice.actions.setFilterError(message));
      return { success: false, error: message };
    } catch (error) {
      const errorMessage = error?.response?.data?.message || 'Failed to create filter';
      dispatch(slice.actions.setFilterError(errorMessage));
      return { success: false, error: errorMessage };
    }
  };
}

// Update an existing custom filter
export function updateCustomFilter(filterId, filterData) {
  return async () => {
    try {
      dispatch(slice.actions.startFilterLoading());

      const response = await customAxios.put(`/custom-filters/${filterId}`, filterData);
      const { status, data, message } = response?.data;
      if (status === 200) {
        dispatch(slice.actions.updateCustomFilter({ id: filterId, ...data }));
        return { success: true, data };
      }
      dispatch(slice.actions.setFilterError(message));
      return { success: false, error: message };
    } catch (error) {
      const errorMessage = error?.response?.data?.message || 'Failed to update filter';
      dispatch(slice.actions.setFilterError(errorMessage));
      return { success: false, error: errorMessage };
    }
  };
}

// Delete a custom filter
export function deleteCustomFilter(filterId) {
  return async () => {
    try {
      dispatch(slice.actions.startFilterLoading());

      const response = await customAxios.delete(`/custom-filters/${filterId}`);
      const { status, message } = response?.data;
      if (status === 200) {
        dispatch(slice.actions.removeCustomFilter(filterId));
        return { success: true };
      }
      dispatch(slice.actions.setFilterError(message));
      return { success: false, error: message };
    } catch (error) {
      const errorMessage = error?.response?.data?.message || 'Failed to delete filter';
      dispatch(slice.actions.setFilterError(errorMessage));
      return { success: false, error: errorMessage };
    }
  };
}

// Get a specific filter by ID
export function getCustomFilterById(filterId) {
  return async () => {
    try {
      dispatch(slice.actions.startFilterLoading());

      const response = await customAxios.get(`/custom-filters/${filterId}`);
      const { status, data, message } = response?.data;
      if (status === 200) {
        dispatch(slice.actions.stopFilterLoading());
        return { success: true, data };
      }
      dispatch(slice.actions.setFilterError(message));
      return { success: false, error: message };
    } catch (error) {
      const errorMessage = error?.response?.data?.message || 'Failed to fetch filter';
      dispatch(slice.actions.setFilterError(errorMessage));
      return { success: false, error: errorMessage };
    }
  };
}

// Apply multiple filters (utility action)
export function applyCustomFilters(filters) {
  return async () => {
    try {
      dispatch(slice.actions.setAppliedFilters(filters));
      return { success: true };
    } catch (error) {
      return { success: false, error: 'Failed to apply filters' };
    }
  };
}

// GET PROFIT SUMMARY
export function getProfitSummary({
  showSharing = true,
  duration = '3WEEK',
  client = null,
  broker = null,
  admin = null,
  startDate = null,
  endDate = null,
}) {
  return async () => {
    try {
      dispatch(slice.actions.startProfitSummaryLoading());

      const queryParams = new URLSearchParams();
      queryParams.append('showSharing', showSharing);
      queryParams.append('duration', duration);

      if (client) queryParams.append('client', client);
      if (broker) queryParams.append('broker', broker);
      if (admin) queryParams.append('admin', admin);

      // Add startDate and endDate for custom duration
      if (duration === 'CUSTOM' && startDate && endDate) {
        queryParams.append('startDate', startDate);
        queryParams.append('endDate', endDate);
      }

      const response = await customAxios.get(`/weekly-report/profit-summary?${queryParams.toString()}`);

      if (response.data && response.data.data) {
        dispatch(slice.actions.getProfitSummarySuccess(response.data.data));
        return { success: true, data: response.data.data };
      }
      throw new Error('Invalid response format');
    } catch (error) {
      const errorMessage = error?.response?.data?.message || error?.message || 'Failed to fetch profit summary';
      dispatch(slice.actions.getProfitSummaryError(errorMessage));
      console.error('Profit Summary API Error:', error);
      return { success: false, error: errorMessage };
    }
  };
}

// GET BROKER SUMMARY
export function getBrokerSummary(brokerId) {
  return async (dispatch) => {
    try {
      dispatch(slice.actions.startLoading());
      const response = await customAxios.get(`/user/broker/${brokerId}/summary`);
      dispatch(slice.actions.stopLoading());
      return { success: true, data: response.data };
    } catch (error) {
      dispatch(slice.actions.hasError(error?.response?.data || error));
      return { success: false, error: error?.response?.data?.message || error?.message || 'Failed to fetch broker summary' };
    }
  };
}

// DELETE BROKER
export function deleteBroker(brokerId) {
  return async (dispatch) => {
    try {
      dispatch(slice.actions.startLoading());
      const response = await customAxios.delete(`/user/broker/${brokerId}`);
      dispatch(slice.actions.stopLoading());
      return { success: true, data: response.data };
    } catch (error) {
      dispatch(slice.actions.hasError(error?.response?.data || error));
      return { success: false, error: error?.response?.data?.message || error?.message || 'Failed to delete broker' };
    }
  };
}

// GET ALL ADMINS
export function getAllAdmins(managerId) {
  return async (dispatch) => {
    try {
      // dispatch(slice.actions.startLoading());
      // Use /user endpoint with managerId to get admins managed by the current user
      const response = await customAxios.get(`/user?managerId=${managerId}&pageSize=1000`);
      // dispatch(slice.actions.stopLoading());
      return { success: true, data: response.data };
    } catch (error) {
      dispatch(slice.actions.hasError(error?.response?.data || error));
      return { success: false, error: error?.response?.data?.message || error?.message || 'Failed to fetch admins' };
    }
  };
}

// CHANGE BROKER MANAGER
export function changeBrokerManager(brokerId, managerId) {
  return async (dispatch) => {
    try {
      dispatch(slice.actions.startLoading());
      const response = await customAxios.put(`/user/broker/${brokerId}/change-manager`, { managerId });
      dispatch(slice.actions.stopLoading());
      return { success: true, data: response.data };
    } catch (error) {
      dispatch(slice.actions.hasError(error?.response?.data || error));
      return { success: false, error: error?.response?.data?.message || error?.message || 'Failed to change manager' };
    }
  };
}

// GET DAILY PL SUMMARY
export function getDailyPLSummary(params = {}) {
  return async (dispatch) => {
    try {
      dispatch(slice.actions.startDailyPLSummaryLoading());

      // Build query parameters
      const queryParams = new URLSearchParams();
      if (params.page) queryParams.append('page', params.page);
      if (params.limit) queryParams.append('limit', params.limit);
      if (params.sortKey) queryParams.append('sortKey', params.sortKey);
      if (params.sortOrder) queryParams.append('sortOrder', params.sortOrder);
      if (params.search) queryParams.append('search', params.search);

      const url = `/trade/daily-pl-summary${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
      const response = await customAxios.get(url);

      // Handle both old and new API response structures
      const responseData = response?.data;
      const pagination = responseData?.pagination || {};
      const data = responseData?.data || [];
      const page = pagination.currentPage || responseData.page || 1;
      const limit = pagination.limit || responseData.limit || 20;
      const totalPages = pagination.totalPages || responseData.totalPages || 1;
      const totalItems = pagination.totalItems || responseData.totalResults || 0;

      if (data) {
        dispatch(slice.actions.getDailyPLSummarySuccess({
          data,
          page,
          limit,
          totalPages,
          totalItems,
          sortKey: params.sortKey,
          sortOrder: params.sortOrder,
        }));
        return { success: true, data };
      }
      dispatch(slice.actions.getDailyPLSummaryError('Failed to fetch daily PL summary'));
      return { success: false, error: 'Failed to fetch daily PL summary' };
    } catch (error) {
      dispatch(slice.actions.getDailyPLSummaryError(error?.response?.data?.message || error.message));
      return { success: false, error: error?.response?.data?.message || error.message };
    }
  };
}

// GET DAILY PL USER DATA BY SCRIPT
export function getDailyPLUserData(scriptId, scriptName, expiry = null, limit = null) {
  return async (dispatch) => {
    try {
      dispatch(slice.actions.startDailyPLUserDataLoading());

      // Build query parameters
      const queryParams = new URLSearchParams();
      if (expiry) queryParams.append('expiry', expiry);
      if (limit) queryParams.append('limit', limit);

      const url = `/trade/daily-pl-user/${scriptId}${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
      const response = await customAxios.get(url);
      const { data } = response?.data;

      if (data) {
        dispatch(slice.actions.getDailyPLUserDataSuccess({ data, scriptName }));
        return { success: true, data };
      }
      dispatch(slice.actions.getDailyPLUserDataError('Failed to fetch daily PL user data'));
      return { success: false, error: 'Failed to fetch daily PL user data' };
    } catch (error) {
      dispatch(slice.actions.getDailyPLUserDataError(error?.response?.data?.message || error.message));
      return { success: false, error: error?.response?.data?.message || error.message };
    }
  };
}
